import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

// #design-pass W3: KPI-Kachel im Design-Stil — Akzent-Leiste links (tone), Icon-Badge
// oben rechts, große tabular-nums-Zahl, Hover-Lift. Klickbar (Deep-Link zur Liste).
type Tone = "neutral" | "good" | "warn" | "alert";

const TONE: Record<Tone, { bar: string; iconBg: string; iconText: string }> = {
  neutral: { bar: "opacity-0", iconBg: "bg-surface2", iconText: "text-text3" },
  good: { bar: "bg-ok", iconBg: "bg-ok-bg", iconText: "text-ok" },
  warn: { bar: "bg-warn", iconBg: "bg-warn-bg", iconText: "text-warn" },
  alert: { bar: "bg-danger", iconBg: "bg-danger-bg", iconText: "text-danger" },
};

export function StatCard({
  to,
  icon: Icon,
  label,
  value,
  sub,
  tone = "neutral",
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
}) {
  const t = TONE[tone];
  return (
    <Link
      to={to}
      data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
      className="group relative overflow-hidden rounded border border-border bg-surface px-[18px] py-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      <span className={cn("absolute left-0 top-0 h-full w-[3px] bg-accent", t.bar)} />
      <div className="mb-3.5 flex items-center justify-between">
        <span className="text-[12.5px] font-semibold text-text2">{label}</span>
        <span className={cn("grid h-8 w-8 place-items-center rounded-lg", t.iconBg, t.iconText)}>
          <Icon className="h-[17px] w-[17px]" />
        </span>
      </div>
      <div className="tnum text-[30px] font-extrabold leading-none tracking-tight text-text">{value}</div>
      {sub && <div className={cn("mt-2.5 text-[12.5px]", tone === "alert" ? "font-semibold text-danger" : "text-text3")}>{sub}</div>}
    </Link>
  );
}
